// PAINT PICKER MODAL — THE house color/gradient/texture surface (owner
// directive, 2026-07-21, verbatim: "In poster properties, when selecting a
// color, the pane expands and causes scroll bars — this is unacceptable ... We
// need a standard color selector modal with color/gradient/texture that opens
// in the middle of the screen regardless of where color is selected. The modal
// should be movable by a user in case they want to use the dropper tool.").
//
// ONE instance, mounted once (PaintPickerModalHost — beside the editor's other
// modals in RibbonMount, and in the ribbon lab harness). Every color entry
// point calls openPaintModal(config) (paintModalController) and this surface
// opens centered with the SAME PaintPicker v2 the flyouts/popovers hosted —
// same prop grammar (value, onChange live grammar, onCommit, allow, opaque,
// linearOnly, recentKey, palette, density), compact tier.
//
// WHY A FLOATING PANEL, NOT AN OVERLAY-BACKED MODAL: the eyedropper's
// canvas-sample fallback (PaintPicker's EyedropOverlay) clicks the fabric
// canvas to pick a color, so a full-screen scrim would block it. This is a
// draggable floating card (no backdrop) — the owner's "movable ... in case they
// want to use the dropper tool" IS the affordance: drag the card aside, then
// sample the artwork under it. Close is ✕ / Esc / Apply only (no
// click-outside, which would fight the eyedropper).
//
// SEMANTICS (educator-predictable, PaintPicker's own footer stays the truth):
//   · Apply  → PaintPicker fires onCommit() then onRequestClose() → close.
//   · Clear  → PaintPicker reverts to ITS open-snapshot in place (stays open).
//   · ✕/Esc  → revert to the paint the modal OPENED with (the same revert Clear
//              uses — write the open snapshot back through the live pipe + one
//              commit) then close; a session that changed nothing closes clean.
//
// Joined to the shared Esc stack (StylesExplorerModal) so Esc pops the top-most
// surface only — correct with a ribbon flyout still open beneath. The
// eyedropper overlay's own window-capture Esc (stopPropagation) still wins
// while sampling, exactly as it does for every other stacked modal.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PaintPicker from './primitives/PaintPicker';
import { registerPaintModalHost } from './paintModalController';
import { pushEscHandler, popEscHandler } from './inspector/text/StylesExplorerModal';
import './PaintPickerModal.css';

const MARGIN = 8; // keep the card this far inside the viewport while dragging

/** Clamp a drag offset so the centered card stays fully on-screen (card bigger
 *  than the viewport on an axis → keep that axis centered). */
function clampOffset(delta, size, viewport) {
  const half = size / 2;
  const lo = MARGIN - viewport / 2 + half;
  const hi = viewport / 2 - MARGIN - half;
  if (lo > hi) return 0;
  return Math.max(lo, Math.min(hi, delta));
}

function PaintPickerModal({ config, onDismiss }) {
  const {
    title,
    fieldId,
    value,
    onChange,
    onCommit,
    allow = ['solid'],
    density = 'compact',
    recentKey = 'global',
    palette,
    linearOnly = false,
    opaque = false,
    allowImport = true,
    onBrowseAll,
  } = config;

  // Revert-on-cancel: snapshot the paint the modal OPENED with. Only revert if
  // the session actually touched the paint (a pure open→Esc is a true no-op —
  // no phantom history entry).
  const openSnapshot = useRef(value);
  const dirty = useRef(false);

  // LIVE value mirror (frozen-value fix, wave-1 verification): config.value is
  // a one-time open snapshot, but PaintPicker derives its whole display (tab,
  // hex/CMYK fields, gradient editor mount) from the value prop — the flyouts
  // always re-fed the live fill each render, and the modal must match or every
  // pick updates the object while the picker face stays stale.
  const [liveValue, setLiveValue] = useState(value);

  const handleChange = (next, meta) => {
    dirty.current = true;
    setLiveValue(next);
    onChange?.(next, meta);
  };

  // Apply's close path (PaintPicker already fired onCommit): just close.
  const closePlain = () => onDismiss?.();
  // ✕ / Esc: revert like Clear (write the open snapshot back, one commit) then
  // close.
  const cancelAndClose = () => {
    if (dirty.current && openSnapshot.current !== undefined) {
      onChange?.(openSnapshot.current, { live: false });
      onCommit?.();
    }
    onDismiss?.();
  };

  // Shared Esc stack — top-most surface only (see StylesExplorerModal). Rides a
  // ref so the handler binds once per mount.
  const cancelRef = useRef(cancelAndClose);
  cancelRef.current = cancelAndClose;
  useEffect(() => {
    const handler = () => cancelRef.current?.();
    pushEscHandler(handler);
    return () => popEscHandler(handler);
  }, []);

  // ---- drag (title bar) ----
  const cardRef = useRef(null);
  const [offset, setOffset] = useState({ dx: 0, dy: 0 });
  const dragRef = useRef(null);

  const onTitleDown = (e) => {
    if (e.button != null && e.button !== 0) return; // primary button only
    if (e.target.closest('.cce-paintmodal-close')) return; // let the ✕ click
    const rect = cardRef.current?.getBoundingClientRect();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseDx: offset.dx,
      baseDy: offset.dy,
      w: rect?.width ?? 0,
      h: rect?.height ?? 0,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onTitleMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = clampOffset(d.baseDx + (e.clientX - d.startX), d.w, window.innerWidth);
    const dy = clampOffset(d.baseDy + (e.clientY - d.startY), d.h, window.innerHeight);
    setOffset({ dx, dy });
  };
  const onTitleUp = (e) => {
    const d = dragRef.current;
    if (d && e.currentTarget.releasePointerCapture) {
      try { e.currentTarget.releasePointerCapture(d.pointerId); } catch { /* ignore */ }
    }
    dragRef.current = null;
  };

  return createPortal(
    <div
      className="cce-paintmodal"
      data-paint-modal={fieldId || title || 'paint'}
      role="dialog"
      aria-label={title}
      ref={cardRef}
      style={{ transform: `translate(-50%, -50%) translate(${offset.dx}px, ${offset.dy}px)` }}
    >
      <div
        className="cce-paintmodal-titlebar"
        onPointerDown={onTitleDown}
        onPointerMove={onTitleMove}
        onPointerUp={onTitleUp}
        onPointerCancel={onTitleUp}
      >
        <span className="cce-paintmodal-title">{title}</span>
        <button
          type="button"
          className="cce-paintmodal-close"
          aria-label="Close"
          title="Close (keeps the paint you had)"
          onClick={cancelAndClose}
        >
          ×
        </button>
      </div>
      <div className="cce-paintmodal-body">
        <PaintPicker
          value={liveValue}
          onChange={handleChange}
          onCommit={onCommit}
          allow={allow}
          density={density}
          recentKey={recentKey}
          palette={palette}
          linearOnly={linearOnly}
          opaque={opaque}
          allowImport={allowImport}
          onRequestClose={closePlain}
          onBrowseAll={onBrowseAll ? () => { closePlain(); onBrowseAll(); } : undefined}
        />
      </div>
    </div>,
    document.body,
  );
}

/** The singleton host — mount ONCE. Registers with the controller so any
 *  color entry point can open the modal via openPaintModal(). Keyed by the
 *  controller's per-open id so opening a NEW target remounts (re-centers +
 *  re-snapshots). */
export function PaintPickerModalHost() {
  const [config, setConfig] = useState(null);
  useEffect(() => registerPaintModalHost(setConfig), []);
  if (!config) return null;
  return (
    <PaintPickerModal
      key={config._id}
      config={config}
      onDismiss={() => setConfig(null)}
    />
  );
}

export default PaintPickerModalHost;
